import moment from "moment";
import { useMemo } from "react";
import {
  getWeekRange,
  resolveMilestones,
} from "../../utils/annual-planner-helpers";

// Groups a list of YYYYMMDD-dated items by which week (0-indexed from
// rangeFirstDay) each one falls into - a single O(n) pass instead of the
// isBetween(weekFirstDay, weekLastDay) scan ListView.js used to repeat once
// per week for every source (training/nutrition phases, notes, milestones,
// volumes, stats). Weeks are always exactly 7 days apart from rangeFirstDay,
// so the week index is a plain day-diff, no search needed.
const bucketByWeek = (items, getUtcDate, rangeFirstDay) => {
  const buckets = new Map();
  (items || []).forEach((item) => {
    const diffDays = moment(String(getUtcDate(item)), "YYYYMMDD").diff(
      rangeFirstDay,
      "days"
    );
    if (diffDays < 0) return;
    const weekIndex = Math.floor(diffDays / 7);
    if (!buckets.has(weekIndex)) buckets.set(weekIndex, []);
    buckets.get(weekIndex).push(item);
  });
  return buckets;
};

// All the per-week data List View's table renders, computed once and
// memoized - previously recomputed on every AnnualPlanner render (e.g.
// toggling a calendar-day selection) even though none of these inputs had
// changed. Mirrors usePhaseSummaryData's extraction out of AnnualPlanner.js,
// for the same reason.
export const useListViewWeeks = ({
  dateFromState,
  startingCalendarDay,
  datesAttachments,
  nutritionDatesAttachments,
  phasesDescriptions,
  clientGoals,
  events,
  athleteStatsState,
  athleteVolumesState,
}) =>
  useMemo(() => {
    const { rangeFirstDay, numberOfWeeks } = getWeekRange(
      dateFromState,
      startingCalendarDay
    );

    // Unlike the "next milestone" status chip, the List view shows the
    // whole year - past milestones stay visible in their week instead of
    // only upcoming ones, so this is the full merge with no todayUtc filter.
    const milestones = resolveMilestones(clientGoals, events);

    const trainingPhaseByWeek = bucketByWeek(
      datesAttachments,
      (attachment) => attachment.dateAttachment,
      rangeFirstDay
    );
    const nutritionPhaseByWeek = bucketByWeek(
      nutritionDatesAttachments,
      (attachment) => attachment.dateAttachment,
      rangeFirstDay
    );
    const commentByWeek = bucketByWeek(
      phasesDescriptions,
      (description) => description.dateAttachment,
      rangeFirstDay
    );
    const milestonesByWeek = bucketByWeek(
      milestones,
      (milestone) => milestone.utcDate,
      rangeFirstDay
    );
    const volumesByWeek = bucketByWeek(
      athleteVolumesState,
      (volume) => volume.utcDate,
      rangeFirstDay
    );
    const statsByWeek = bucketByWeek(
      athleteStatsState,
      (stat) => stat.utcDate,
      rangeFirstDay
    );

    return Array.from(Array(numberOfWeeks).keys(), (weekNumber) => ({
      weekFirstDay: moment(rangeFirstDay).add(weekNumber, "weeks"),
      weekTrainingPhase: trainingPhaseByWeek.get(weekNumber)?.[0] ?? null,
      weekNutritionPhase: nutritionPhaseByWeek.get(weekNumber)?.[0] ?? null,
      weekComment: commentByWeek.get(weekNumber)?.[0] ?? null,
      weekMilestones: milestonesByWeek.get(weekNumber) ?? [],
      weekVolumes: volumesByWeek.get(weekNumber) ?? [],
      weekStats: statsByWeek.get(weekNumber) ?? [],
    }));
  }, [
    dateFromState,
    startingCalendarDay,
    datesAttachments,
    nutritionDatesAttachments,
    phasesDescriptions,
    clientGoals,
    events,
    athleteStatsState,
    athleteVolumesState,
  ]);
